/* eslint-disable no-console */
import { Col, Row } from 'antd';
import { useAppDispatch, useAppSelector } from 'modules/hooks/appHooks';
import { useSidePopout } from 'modules/layout';
import { UserCredentials } from 'modules/types/UserCredentials';
import { BodyText, HeadlineTitle, InputWithLabel, Span } from 'modules/ui';
import { FormButtons, FormContainer, FormPane } from 'modules/ui/form';
import React, { useCallback, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { LoginSelectors, LoginThunks } from '../redux';
import { resetError } from '../redux/slice';
import { FooterButtons } from './FooterButtons';
import { DescriptionWrapper, StyledList } from './styled';
import { TfaMail } from '../../tfaMail';
import { TfaTypes, UserLoginDto } from '../../../../types/User';
import { TfaForm } from '../../tfa';
import { TfaChoice } from '../../tfaChoice';

interface LoginFormProps {}

export const LoginForm: React.FC<LoginFormProps> = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const loginError = useAppSelector(LoginSelectors.selectLoginError);
  const { changePopoutContainer } = useSidePopout();

  const handleLogin = useCallback(
    (event) => {
      event.preventDefault();
      const formData = new FormData(event.target);
      const inputObject = Object.fromEntries(formData) as UserCredentials; // convert the FormData object to a JSON object
      dispatch(LoginThunks.login(inputObject)).then((res) => {
        if (res.type === 'auth/login/fulfilled') {
          if (
            (res.payload as UserLoginDto).typeTfa ===
            TfaTypes.GOOGLE_AUTHENTICATOR
          ) {
            changePopoutContainer({
              newContainer: (
                <TfaForm qrCode={(res.payload as UserLoginDto).qrCode!} />
              ),
              urlKey: 'authorize',
            });
          } else if ((res.payload as UserLoginDto).typeTfa === TfaTypes.MAIL) {
            changePopoutContainer({
              newContainer: <TfaMail />,
              urlKey: 'authorize',
            });
          } else {
            changePopoutContainer({
              newContainer: (
                <TfaChoice userLoginDto={res.payload as UserLoginDto} />
              ),
              urlKey: 'authorize',
            });
          }
        }
      });
    },
    [changePopoutContainer, dispatch],
  );

  useEffect(
    () => () => {
      dispatch(resetError());
    },
    [dispatch],
  );

  return (
    <Row>
      <Col span={24} md={14}>
        <HeadlineTitle>{t('landingPage.loginForm.login')}</HeadlineTitle>
        <FormContainer onSubmit={handleLogin}>
          <FormPane>
            <InputWithLabel
              name="username"
              label={t('landingPage.loginForm.username')}
              placeholderText={t('landingPage.loginForm.usernamePlaceholder')}
              autoFocus
            />
            <InputWithLabel
              type="password"
              name="password"
              label={t('landingPage.loginForm.password')}
              placeholderText={t('landingPage.loginForm.passwordPlaceholder')}
              errorText={
                loginError ? t('landingPage.loginForm.loginError') : undefined
              }
              autoComplete="off"
            />
          </FormPane>
          <FormButtons ariaLabel={"Prijava"} text={t('landingPage.loginForm.login')} centered />
          <FooterButtons />
        </FormContainer>
      </Col>
      <Col span={24} md={{ span: 9, offset: 1 }}>
        <DescriptionWrapper>
          <BodyText>
            <Trans
              i18nKey="landingPage.loginForm.descriptionText"
              components={{
                upperCase: <Span textTransform="uppercase" />,
              }}
            />
          </BodyText>
          <StyledList>
            <li>
              <BodyText>
                {t('landingPage.loginForm.contractsDetailsText')}
              </BodyText>
            </li>
            <li>
              <BodyText>
                {t('landingPage.loginForm.billsDetailsText')}{' '}
              </BodyText>
            </li>
            <li>
              <BodyText>
                {t('landingPage.loginForm.financingDetailsText')}
              </BodyText>
            </li>
          </StyledList>
          <BodyText>{t('landingPage.loginForm.disclaimer')}</BodyText>
        </DescriptionWrapper>
      </Col>
    </Row>
  );
};
